import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Link2, Users } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function ShareModal({ isOpen, onClose, mapId, mapTitle }) {
  const [loading, setLoading] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerateLink = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/maps/${mapId}/share`);
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/collab/${res.data.share_token}`;
      setShareLink(link);
    } catch (err) {
      toast.error('Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleClose = () => {
    setShareLink(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[90vw] max-w-[380px] p-0 bg-white border-0 shadow-2xl rounded-xl overflow-hidden">
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <DialogHeader>
            <DialogTitle className="font-heading text-base font-medium text-gray-900 flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
              Share & Collaborate
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 mt-1 truncate">
            Invite others to edit "{mapTitle}"
          </p>
        </div>

        {/* Content */}
        <div className="px-5 pb-5">
          {!shareLink ? (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 leading-relaxed">
                  Generate a link to invite collaborators. They can edit and see changes live.
                </p>
              </div>
              
              <Button
                onClick={handleGenerateLink}
                disabled={loading}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white h-10 text-sm"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Generating...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Link2 className="w-4 h-4" strokeWidth={1.5} />
                    Generate Share Link
                  </span>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Clickable link box */}
              <button
                onClick={handleCopyLink}
                className={`w-full rounded-lg p-3 transition-all duration-200 text-left ${
                  copied 
                    ? 'bg-green-50 border border-green-300' 
                    : 'bg-gray-50 border border-gray-200 hover:border-cobalt/40 hover:bg-gray-100'
                }`}
              >
                <p className="font-mono text-[11px] text-gray-600 break-all leading-relaxed">
                  {shareLink}
                </p>
                <p className={`text-xs font-medium mt-2 ${copied ? 'text-green-600' : 'text-gray-400'}`}>
                  {copied ? '✓ Copied!' : 'Click to copy'}
                </p>
              </button>

              {/* Info box */}
              <div className="flex items-center gap-2.5 p-2.5 bg-blue-50 rounded-lg">
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <Users className="w-3.5 h-3.5 text-blue-600" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-blue-900">Real-time sync</p>
                  <p className="text-[11px] text-blue-700">Guests can edit live</p>
                </div>
              </div>

              <p className="text-[11px] text-gray-400 text-center">
                Expires in 7 days
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
